// src/constants/nav.js

import { armScrollRestoreFromListUrl } from "../src/hooks/useScrollRestoration.js";

const LIST_RETURN_PREFIX = "wt:list-return:";

export function canGoBack() {
  try {
    // Bei React Router v6 ist window.history.state.idx meist vorhanden.
    // Fallback: window.history.length > 1 (funktioniert auch bei Reloads).
    const idx = window.history?.state?.idx;
    const len = window.history?.length ?? 0;
    return (typeof idx === "number" && idx > 0) || len > 1;
  } catch {
    return false;
  }
}

export function smartBack(navigate, fallbacks = ["/"]) {
  const paths = Array.isArray(fallbacks) ? fallbacks.filter(Boolean) : [fallbacks];
  const target = paths[0] || "/";

  try {
    if (canGoBack()) {
      window.history.back();
    } else {
      navigate(target, { replace: true });
    }
  } catch (err) {
    console.warn("smartBack fallback:", err);
    navigate(target, { replace: true });
  }
}

/**
 * Normalize list path root, e.g. "/service/" → "/service"
 * @param {string} listPath
 */
export function normalizeListPath(listPath) {
  const raw = String(listPath || "").trim();
  if (!raw.startsWith("/")) return "";
  // strip query/hash
  const pathOnly = raw.split("?")[0].split("#")[0];
  if (pathOnly.length > 1 && pathOnly.endsWith("/")) return pathOnly.slice(0, -1);
  return pathOnly;
}

/**
 * Accept only same-origin relative WireTrack list URLs under an expected list path.
 * Rejects external origins, protocol-relative, javascript:, etc.
 *
 * @param {string} url
 * @param {string} listPath e.g. "/service"
 * @returns {string|null} safe pathname+search or null
 */
export function sanitizeListReturnUrl(url, listPath) {
  const expected = normalizeListPath(listPath);
  if (!expected) return null;
  const raw = String(url || "").trim();
  if (!raw) return null;
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(raw)) return null; // absolute with scheme
  if (raw.startsWith("//")) return null;
  if (/[\u0000-\u001F]/.test(raw)) return null;
  if (!raw.startsWith("/")) return null;

  let pathname = raw;
  let search = "";
  const qIdx = raw.indexOf("?");
  if (qIdx >= 0) {
    pathname = raw.slice(0, qIdx);
    search = raw.slice(qIdx);
  }
  const hashIdx = pathname.indexOf("#");
  if (hashIdx >= 0) pathname = pathname.slice(0, hashIdx);
  pathname = normalizeListPath(pathname);
  if (pathname !== expected) return null;
  // Disallow nested paths masquerading as list root (e.g. /service/123)
  // expected already equals pathname only for exact list root.
  return search ? `${pathname}${search}` : pathname;
}

function storageKeyForList(listPath) {
  const p = normalizeListPath(listPath);
  return p ? `${LIST_RETURN_PREFIX}${p}` : null;
}

/**
 * Remember the canonical list URL for an explicit "…liste" button later.
 * @param {string} listPath
 * @param {string} fullUrl pathname + search
 */
export function rememberListReturnUrl(listPath, fullUrl) {
  const key = storageKeyForList(listPath);
  const safe = sanitizeListReturnUrl(fullUrl, listPath);
  if (!key || !safe) return false;
  try {
    sessionStorage.setItem(key, safe);
    return true;
  } catch {
    return false;
  }
}

/**
 * @param {string} listPath
 * @returns {string|null}
 */
export function getListReturnUrl(listPath) {
  const key = storageKeyForList(listPath);
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(key);
    return sanitizeListReturnUrl(raw || "", listPath);
  } catch {
    return null;
  }
}

export function clearListReturnUrl(listPath) {
  const key = storageKeyForList(listPath);
  if (!key) return;
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function clearAllListReturnUrls() {
  try {
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(LIST_RETURN_PREFIX)) keys.push(k);
    }
    for (const k of keys) sessionStorage.removeItem(k);
  } catch {
    /* ignore */
  }
}

/**
 * Explicit list navigation (Serviceliste / Kundenliste / …).
 * Uses remembered filtered list URL when valid; otherwise list root fallback.
 * Distinct from smartBack (history).
 *
 * When a remembered URL is used, arms scroll restoration so PUSH does not
 * force the list to the top (same UX as browser Back for that list context).
 * Fallback to bare list root starts at top.
 *
 * @param {Function} navigate
 * @param {string} listPath
 * @param {{ replace?: boolean }} [opts]
 */
export function navigateToList(navigate, listPath, opts = {}) {
  const fallback = normalizeListPath(listPath) || "/";
  const remembered = getListReturnUrl(fallback);
  const target = remembered || fallback;
  if (remembered) {
    armScrollRestoreFromListUrl(remembered);
  }
  navigate(target, { replace: !!opts.replace });
}

/**
 * Convenience: remember current location if it belongs to listPath.
 * @param {string} listPath
 * @param {{ pathname: string, search?: string }} location
 */
export function rememberCurrentListLocation(listPath, location) {
  const path = normalizeListPath(listPath);
  if (!path || normalizeListPath(location?.pathname) !== path) return false;
  const search = location?.search || "";
  return rememberListReturnUrl(path, `${path}${search || ""}`);
}
